/**
 * Web entry point for React Native Web
 * This file runs your React Native app in web browsers
 */

// CRITICAL: Patch NativeModules BEFORE React Native loads
if (typeof window !== 'undefined' && !window.__NATIVE_MODULES_PATCHED__) {
  // Create a global NativeModules that React Native will use
  window.__REACT_NATIVE_GLOBAL_NATIVE_MODULES__ = {
    PlatformConstants: {
      getEnforcing: () => false,
      enforce: () => false,
      forceTouchAvailable: false,
      interfaceIdiom: 'handset',
      isTesting: false,
      osVersion: '0.0.0',
      systemName: 'Web',
    },
  };
  window.__NATIVE_MODULES_PATCHED__ = true;
}

// Import polyfills
import './web-polyfills';

import { AppRegistry } from 'react-native';
import React from 'react';
import { View, Text, StyleSheet, NativeModules } from 'react-native';

// Patch TurboModuleRegistry if it exists on react-native
if (typeof require !== 'undefined') {
  try {
    const RN = require('react-native');
    if (RN && !RN.TurboModuleRegistry) {
      RN.TurboModuleRegistry = window.TurboModuleRegistry || {
        get: function(name) { return null; },
        getEnforcing: function(name) {
          console.warn('TurboModuleRegistry.getEnforcing(' + name + ') called on web - returning mock');
          return { initialize: function() {} };
        },
      };
    }
  } catch (e) {
    // Ignore
  }
}

// IMMEDIATELY patch NativeModules after import
(function() {
  'use strict';
  if (typeof NativeModules !== 'undefined') {
    // Create PlatformConstants if it doesn't exist
    if (!NativeModules.PlatformConstants) {
      NativeModules.PlatformConstants = window.__REACT_NATIVE_GLOBAL_NATIVE_MODULES__?.PlatformConstants || {
        getEnforcing: () => false,
        enforce: () => false,
      };
    }
    
    // Force define getEnforcing as non-configurable
    try {
      Object.defineProperty(NativeModules.PlatformConstants, 'getEnforcing', {
        value: () => false,
        writable: false,
        enumerable: true,
        configurable: false,
      });
    } catch (e) {
      // Already defined, ignore
    }
    
    try {
      Object.defineProperty(NativeModules.PlatformConstants, 'enforce', {
        value: () => false,
        writable: false,
        enumerable: true,
        configurable: false,
      });
    } catch (e) {
      // Already defined, ignore
    }
  }
})();

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <View style={styles.errorContainer}>
          <Text style={styles.errorTitle}>Something went wrong</Text>
          <Text style={styles.errorMessage}>
            {this.state.error?.toString() || 'Unknown error'}
          </Text>
          <Text style={styles.errorHint}>
            Please check the browser console (F12) for more details
          </Text>
        </View>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#fff',
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#f44336',
    marginBottom: 10,
  },
  errorMessage: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 10,
  },
  errorHint: {
    fontSize: 12,
    color: '#999',
    textAlign: 'center',
  },
});

// Wrapper component with error boundary
const AppWithErrorBoundary = () => {
  let App;
  try {
    App = require('./App').default;
  } catch (error) {
    console.error('Failed to load App component:', error);
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorTitle}>Failed to load app</Text>
        <Text style={styles.errorMessage}>{error.toString()}</Text>
      </View>
    );
  }

  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
};

// Register the app component
console.log('🚀 Registering BookAPT app...');
AppRegistry.registerComponent('BookAPT', () => AppWithErrorBoundary);

// Wait for DOM to be ready
const rootElement = document.getElementById('root');
if (!rootElement) {
  console.error('❌ Root element not found!');
  document.body.innerHTML = '<div style="padding: 20px; color: red;">Error: Root element (#root) not found in HTML</div>';
} else {
  console.log('✅ Root element found, running application...');
  // Run the app in the browser
  try {
    AppRegistry.runApplication('BookAPT', {
      initialProps: {},
      rootTag: rootElement,
    });
    console.log('✅ App registered and running!');
  } catch (error) {
    console.error('❌ Failed to run application:', error);
    rootElement.innerHTML = `
      <div style="padding: 40px; text-align: center; color: #f44336;">
        <h2>Failed to Start App</h2>
        <p>${error.toString()}</p>
        <p style="font-size: 12px; color: #999;">Check browser console (F12) for details</p>
      </div>
    `;
  }
}
